import React, {Fragment, useState} from 'react';
import {Dialog, Transition} from '@headlessui/react';
import Icon from '@/shared/components/Icon';
import {toast} from 'react-toastify';
import PaymentWidget from '@/modules/shop/components/PaymentWidget';
import AddressForm, {type AddressData} from '@/modules/shop/components/AddressForm';
import BioMatchEnrollmentService from '../services/bioMatchEnrollmentService';
import {BIOMATCH_DASHBOARD_QUERY_KEY} from '../hooks/useBioMatchMarkers';
import {queryClient} from '@/shared/contexts/QueryProvider';
import {useAuth} from '@/modules/auth/hooks/useAuth';
import {LATEST_ORDER_QUERY_KEY} from '@/modules/patient-dashboard/hooks/useLatestOrder';
import {logError} from '@/shared/utils/logger';
import {randomUUID} from '@/shared/utils/uuid';
import {dollars} from '@/shared/utils/money';

/**
 * Patient-pays BioMatch enrollment checkout (4c).
 *
 *   1. Mounts the Fortis Elements widget against the BioMatch ticket
 *      intention so the patient can enter card details.
 *   2. On `onTicketReady`, calls the finalize endpoint with the
 *      ticket id + the recommendation_id. The BE re-validates the
 *      amount server-side, charges, and creates the enrollment +
 *      storefront_order in one transaction.
 *   3. Surfaces success/decline/failure back through toasts.
 */

interface Props {
    open: boolean;
    recommendationId: number;
    programName: string;
    bodyText?: string | null;
    patientAmountCents: number;
    onSuccess: () => void;
    onClose: () => void;
}

const BioMatchCheckoutModal: React.FC<Props> = ({
    open,
    recommendationId,
    programName,
    bodyText,
    patientAmountCents,
    onSuccess,
    onClose,
}) => {
    const {user} = useAuth();
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [checkoutAttemptId, setCheckoutAttemptId] = useState(() => randomUUID());
    // Default unchecked — compliance-safe opt-in. Patient must
    // explicitly authorize the recurring 90-day auto-charge.
    const [saveCard, setSaveCard] = useState(false);
    // Ship-to for the kit — prefilled from the profile; whatever the
    // patient types here wins and is written back to the profile by
    // the BE on a successful charge.
    const [shippingAddress, setShippingAddress] = useState<AddressData>(() => ({
        line1: user?.userInfo?.address1 ?? '',
        line2: user?.userInfo?.address2 ?? '',
        city: user?.userInfo?.city ?? '',
        state: user?.userInfo?.state ?? '',
        zip: user?.userInfo?.zip ?? '',
    }));

    const handleTicket = async (ticketId: string) => {
        // Validate before charging — the BE would 422 pre-charge anyway,
        // but catching it here spares the patient a spent card ticket.
        if (
            !shippingAddress.line1.trim() ||
            !shippingAddress.city.trim() ||
            !shippingAddress.state.trim() ||
            !shippingAddress.zip.trim()
        ) {
            setError('Please complete your shipping address (street, city, state, ZIP) before paying.');
            return;
        }
        setSubmitting(true);
        setError(null);
        try {
            const result = await BioMatchEnrollmentService.finalizeCheckout(
                recommendationId,
                ticketId,
                checkoutAttemptId,
                saveCard,
                {
                    line1: shippingAddress.line1.trim(),
                    line2: shippingAddress.line2.trim() || null,
                    city: shippingAddress.city.trim(),
                    state: shippingAddress.state.trim(),
                    zip: shippingAddress.zip.trim(),
                }
            );
            if (result.paymentStatus === 'CAPTURED') {
                // Latest-purchase card caches for 5 minutes — invalidate so
                // the new enrollment order shows on the dashboard right away.
                void queryClient.invalidateQueries({queryKey: LATEST_ORDER_QUERY_KEY});
                toast.success(`Payment ${dollars(result.patientAmountCents)} accepted. You're enrolled.`);
                // The enrollment may consume a BioMatch offer server-side —
                // refresh the assembled dashboard view right away.
                void queryClient.invalidateQueries({queryKey: BIOMATCH_DASHBOARD_QUERY_KEY});
                onSuccess();
            } else {
                setError('Payment is pending. Check back shortly.');
            }
        } catch (err) {
            setCheckoutAttemptId(randomUUID());
            const status = (err as {status?: number} | undefined)?.status;
            const data = (err as {data?: {error?: string}} | undefined)?.data;
            if (status === 402) {
                setError(data?.error ?? 'Payment was declined. Please try a different card.');
            } else if (status === 422) {
                setError(data?.error ?? 'Could not complete enrollment.');
            } else {
                logError('BioMatch checkout finalize failed', err);
                setError('Something went wrong completing your enrollment. Please try again.');
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Transition appear show={open} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={submitting ? () => undefined : onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-200"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-150"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0">
                    <div className="fixed inset-0 bg-black/40" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-200"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-150"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95">
                            <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                                <div className="flex items-start justify-between gap-3">
                                    <Dialog.Title className="text-lg font-semibold text-mld-text-primary">
                                        Complete enrollment in {programName}
                                    </Dialog.Title>
                                    <button
                                        type="button"
                                        onClick={onClose}
                                        disabled={submitting}
                                        aria-label="Close"
                                        className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50">
                                        <Icon name="xmark" className="h-4 w-4" />
                                    </button>
                                </div>

                                {bodyText && (
                                    <p className="mt-3 text-sm leading-relaxed text-mld-text-secondary">{bodyText}</p>
                                )}

                                <div className="mt-4 rounded-md bg-mld-panel-bg p-3 text-sm">
                                    <div className="flex items-center justify-between">
                                        <span className="text-mld-text-secondary">Your portion</span>
                                        <span className="font-semibold text-mld-text-primary">
                                            {dollars(patientAmountCents)}
                                        </span>
                                    </div>
                                </div>

                                {/* Recurring-billing consent. Default unchecked —
                                    explicit opt-in is required for stored-card
                                    auto-charge. Patient checking this authorizes
                                    MLD to charge this card every 90 days for
                                    quarterly testing kits. */}
                                <label className="mt-4 flex items-start gap-2 rounded-md border border-mld-panel-border p-3 text-xs text-mld-text-secondary">
                                    <input
                                        type="checkbox"
                                        checked={saveCard}
                                        onChange={(e) => setSaveCard(e.target.checked)}
                                        disabled={submitting}
                                        className="mt-0.5 h-4 w-4 rounded border-gray-300 text-mld-primary focus:ring-mld-primary"
                                    />
                                    <span>
                                        <span className="block font-medium text-mld-text-primary">
                                            Save my card for the next 90-day kit
                                        </span>
                                        I authorize MLD to securely store this card and charge{' '}
                                        <span className="font-semibold">{dollars(patientAmountCents)}</span> every 90
                                        days for the next quarterly testing cycle. I can cancel anytime — leave this
                                        unchecked and I&apos;ll be re-prompted at the next cycle.
                                    </span>
                                </label>

                                <div className="mt-4 rounded-md border border-mld-panel-border p-3">
                                    <AddressForm
                                        value={shippingAddress}
                                        onChange={setShippingAddress}
                                        label="Shipping address for your kit"
                                    />
                                </div>

                                <div className="mt-4 min-h-[200px]">
                                    <PaymentWidget
                                        onTokenReady={handleTicket}
                                        onError={(msg) => setError(msg)}
                                        disabled={submitting}
                                        containerId="biomatch-fortis-elements"
                                        createIntention={() => BioMatchEnrollmentService.createCheckoutTicket()}
                                    />
                                </div>

                                {submitting && (
                                    <div className="mt-3 flex items-center gap-2 text-sm text-mld-text-secondary">
                                        <Icon name="spinner" spin className="h-4 w-4" />
                                        Processing payment…
                                    </div>
                                )}

                                {error && (
                                    <div className="mt-3 rounded-md border border-mld-danger/20 bg-mld-danger/5 px-3 py-2 text-sm text-mld-danger">
                                        {error}
                                    </div>
                                )}
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};

export default BioMatchCheckoutModal;
